import { useFeatureFlagEnabled } from "posthog-js/react";
import { useCallback, useMemo } from "react";
import { useLocalState, useLocalStore } from "../../../hooks/useLocalStore";
import { useNewRelic } from "../../../hooks/useNewRelic";

type FeatureFlag = {
  label: string;
  description: string;
  forceReloadOnChange?: boolean;
  postHogKey?: string;
};
const experimentalCollectForm: FeatureFlag = {
  label: "New Collect Form",
  description: "Enable experimental Collect features."
};

export const FEATURE_FLAGS = {
  experimentalCollectForm,
  experimentalSketchTool: {
    label: "Experimental Sketch Tool",
    description:
      "Enable the experimental sketch tool in Undermaps. DANGER: This feature is not yet complete and may cause data loss. Will force the page to reload.",
    forceReloadOnChange: true
  } as FeatureFlag,
  pdfExport: {
    label: "PDF Export",
    description: "Enable PDF export functionality.",
    postHogKey: "pdf_export",
    forceReloadOnChange: true
  } as FeatureFlag
};

export type FeatureFlags = keyof typeof FEATURE_FLAGS;

export const useFeatureFlags = () => {
  const { getItem } = useLocalStore();
  const featureFlags = useMemo(
    () =>
      Object.entries(FEATURE_FLAGS).map(([key, flag]) => {
        const enabled = toBoolean(getItem(`featureFlag_${key}`));
        return { ...flag, key, enabled };
      }),
    [getItem]
  );

  const isFeatureFlagEnabled = useCallback(
    (featureFlagKey: FeatureFlags) => {
      const flag = featureFlags.find((flag) => flag.key === featureFlagKey);
      return flag?.enabled;
    },

    [featureFlags]
  );

  return { isFeatureFlagEnabled, featureFlags };
};

export const useFeatureFlag = (featureFlag: FeatureFlags) => {
  const [featureFlagValue, setFeatureFlagValue] = useLocalState(
    `featureFlag_${featureFlag}`
  );

  const { setCustomAttribute } = useNewRelic();

  const featureFlagConfig = FEATURE_FLAGS[featureFlag];

  const postHogFeatureEnabled = useFeatureFlagEnabled(
    featureFlagConfig?.postHogKey ?? ""
  );

  const isEnabled = featureFlagValue
    ? toBoolean(featureFlagValue)
    : postHogFeatureEnabled;

  const setEnabled = useCallback(
    (enabled: boolean | undefined) => {
      if (enabled == null) {
        setCustomAttribute(`featureFlag_${featureFlag}`, null);
        setFeatureFlagValue(undefined);
      } else {
        setCustomAttribute(`featureFlag_${featureFlag}`, enabled);
        setFeatureFlagValue(toString(enabled));
      }

      if (featureFlagConfig.forceReloadOnChange) {
        window.location.reload();
      }
    },
    [
      featureFlag,
      featureFlagConfig.forceReloadOnChange,
      setCustomAttribute,
      setFeatureFlagValue
    ]
  );

  return { isEnabled, setEnabled };
};

const toBoolean = (value: string | null | undefined): boolean | undefined =>
  value === "true" ? true : value === "false" ? false : undefined;

const toString = (value: boolean): string => (value ? "true" : "false");
